import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Truck, CaretDown, CaretUp, Package, Scales, CheckCircle, XCircle, Clock, Warning, MagnifyingGlass, Ghost } from "@phosphor-icons/react";
import { getApiErrorMessage } from "@/lib/apiError";
import { PageHeader } from "@/components/PageHeader";
import { EncomendaOrdersTable, formatEncomendaNum as formatNum } from "@/components/EncomendaOrdersTable";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FILTER_META = {
  zombie: { label: "Encomendas Zombie (+60 dias úteis)", icon: Ghost, color: "text-red-600" },
  delayed: { label: "Atraso (+6 dias úteis)", icon: Clock, color: "text-orange-500" },
  shortage: { label: "Falta de Stock", icon: Warning, color: "text-red-500" },
  cnt: { label: "Recolha no Cliente (Reclamação)", icon: Truck, color: "text-blue-600" },
};


export default function Encomendas() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [search, setSearch] = useState("");
  const [filterModal, setFilterModal] = useState(null); // 'zombie' | 'delayed' | 'shortage' | 'cnt' | null

  useEffect(() => {
    const fetchEncomendas = async () => {
      try {
        const response = await axios.get(`${API}/encomendas`);
        setData(response.data);
      } catch (error) {
        toast.error("Erro ao carregar Encomendas: " + getApiErrorMessage(error));
      } finally {
        setLoading(false);
      }
    };
    fetchEncomendas();
  }, []);

  const allClients = data?.clients || [];

  // Itens que correspondem à categoria clicada na legenda (Zombie/Atraso/Falta de
  // Stock/Recolha), agregados de todos os clientes - mostra exatamente que cliente e que
  // material está nessa situação, num único modal.
  const filteredItems = useMemo(() => {
    if (!filterModal) return [];
    const rows = [];
    allClients.forEach((client) => {
      (client.items || []).forEach((item, itemIdx) => {
        const matches =
          (filterModal === 'zombie' && item.is_zombie) ||
          (filterModal === 'delayed' && item.is_delayed) ||
          (filterModal === 'shortage' && item.is_stock_shortage) ||
          (filterModal === 'cnt' && item.is_cnt_pickup);
        if (matches) {
          rows.push({ ...item, clientName: client.client_name, clientId: client.client_id, rowKey: `${client.client_name}-${itemIdx}` });
        }
      });
    });
    return rows;
  }, [filterModal, allClients]);

  // Contador em vivo: nº de artigos em cada categoria de alerta, para mostrar na legenda
  // sem precisar de abrir o modal.
  const categoryCounts = useMemo(() => {
    const counts = { zombie: 0, delayed: 0, shortage: 0, cnt: 0 };
    allClients.forEach((client) => {
      (client.items || []).forEach((item) => {
        if (item.is_zombie) counts.zombie += 1;
        if (item.is_delayed) counts.delayed += 1;
        if (item.is_stock_shortage) counts.shortage += 1;
        if (item.is_cnt_pickup) counts.cnt += 1;
      });
    });
    return counts;
  }, [allClients]);

  const clients = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return allClients;
    return allClients.filter(c => {
      if (c.client_name?.toLowerCase().includes(term)) return true;
      return (c.items || []).some(i =>
        i.artigo?.toLowerCase().includes(term) || i.descricao?.toLowerCase().includes(term) ||
        i.order_number?.toLowerCase().includes(term) || i.order_ref?.toLowerCase().includes(term)
      );
    });
  }, [allClients, search]);

  return (
    <div className="min-h-screen bg-stone-50 pb-10" data-testid="encomendas-page">
      <div className="bg-white border-b border-stone-200 px-4 py-4 sticky top-0 z-10">
        <PageHeader
          icon={Truck}
          title="Encomendas Pendentes"
          subtitle="Pedidos diários carregados por cliente (GPP)"
        />
      </div>

      <div className="max-w-6xl mx-auto px-4 md:px-8 py-6 space-y-4">
        {data?.last_updated && (
          <p className="text-xs text-stone-400 text-center" data-testid="encomendas-last-updated">
            Carregado em {new Date(data.last_updated).toLocaleString('pt-PT')}
          </p>
        )}

        {allClients.length > 0 && (
          <>
            <div className="relative">
              <MagnifyingGlass size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-stone-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Pesquisar por cliente, referência/descrição do produto ou nº de encomenda..."
                className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-stone-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#009392]/40 focus:border-[#009392]"
                data-testid="encomendas-search-input"
              />
            </div>

            {/* Legenda de Símbolos - clicável: cada ícone abre um modal com a lista exata de
                clientes e materiais nessa situação (Zombie/Atraso/Falta de Stock/Recolha) */}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[11px] text-stone-500 px-1" data-testid="encomendas-legend">
              <button
                type="button"
                onClick={() => setFilterModal('zombie')}
                className="inline-flex items-center gap-1.5 hover:text-stone-800 hover:underline"
                data-testid="encomendas-legend-filter-zombie"
              >
                <Ghost size={13} weight="fill" className="text-red-600 flex-shrink-0" />
                Encomenda Zombie (+60 dias úteis) <span className="font-semibold" data-testid="encomendas-count-zombie">({categoryCounts.zombie})</span>
              </button>
              <button
                type="button"
                onClick={() => setFilterModal('delayed')}
                className="inline-flex items-center gap-1.5 hover:text-stone-800 hover:underline"
                data-testid="encomendas-legend-filter-delayed"
              >
                <Clock size={13} weight="fill" className="text-orange-500 flex-shrink-0" />
                Atraso (+6 dias úteis) <span className="font-semibold" data-testid="encomendas-count-delayed">({categoryCounts.delayed})</span>
              </button>
              <button
                type="button"
                onClick={() => setFilterModal('shortage')}
                className="inline-flex items-center gap-1.5 hover:text-stone-800 hover:underline"
                data-testid="encomendas-legend-filter-shortage"
              >
                <Warning size={13} weight="fill" className="text-red-500 flex-shrink-0" />
                Falta de Stock <span className="font-semibold" data-testid="encomendas-count-shortage">({categoryCounts.shortage})</span>
              </button>
              <button
                type="button"
                onClick={() => setFilterModal('cnt')}
                className="inline-flex items-center gap-1.5 hover:text-stone-800 hover:underline"
                data-testid="encomendas-legend-filter-cnt"
              >
                <Truck size={13} weight="fill" className="text-blue-600 flex-shrink-0" />
                Recolha no Cliente (Reclamação) <span className="font-semibold" data-testid="encomendas-count-cnt">({categoryCounts.cnt})</span>
              </button>
            </div>
          </>
        )}

        {loading ? (
          <p className="text-center text-stone-400 py-10">A carregar...</p>
        ) : allClients.length === 0 ? (
          <div className="gresart-card text-center py-10" data-testid="encomendas-empty-state">
            <Package size={40} className="mx-auto text-stone-300 mb-3" />
            <p className="text-stone-500 font-medium">Nenhuma encomenda pendente carregada.</p>
            <p className="text-stone-400 text-sm mt-1">Carregue os PDFs em Perfil &gt; Configurações &gt; Carga de Encomendas Diárias.</p>
          </div>
        ) : clients.length === 0 ? (
          <div className="gresart-card text-center py-10" data-testid="encomendas-no-results">
            <MagnifyingGlass size={40} className="mx-auto text-stone-300 mb-3" />
            <p className="text-stone-500 font-medium">Nenhum resultado para "{search}".</p>
          </div>
        ) : (
          clients.map((client, idx) => {
            const isOpen = expanded === idx;
            const isMatched = !!client.client_id;
            return (
              <div key={idx} className="gresart-card p-0 overflow-hidden" data-testid={`encomenda-client-card-${idx}`}>
                <button
                  type="button"
                  onClick={() => setExpanded(isOpen ? null : idx)}
                  className="w-full flex items-center justify-between gap-3 p-4 text-left hover:bg-stone-50 transition-colors"
                  data-testid={`encomenda-client-toggle-${idx}`}
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-bold text-stone-900 leading-tight truncate">{client.client_name}</h3>
                      {isMatched ? (
                        <CheckCircle size={16} weight="fill" className="text-emerald-500 flex-shrink-0" data-testid={`encomenda-matched-${idx}`} />
                      ) : (
                        <XCircle size={16} weight="fill" className="text-amber-400 flex-shrink-0" data-testid={`encomenda-unmatched-${idx}`} />
                      )}
                      {client.has_zombie_items && (
                        <span title="Tem encomendas Zombie (+60 dias úteis em carteira) - verificar se devem ser anuladas" data-testid={`encomenda-client-zombie-${idx}`}>
                          <Ghost size={16} weight="fill" className="text-red-600 flex-shrink-0" />
                        </span>
                      )}
                      {client.has_delayed_items && (
                        <span title="Tem artigos com mais de 6 dias" data-testid={`encomenda-client-delayed-${idx}`}>
                          <Clock size={16} weight="fill" className="text-orange-500 flex-shrink-0" />
                        </span>
                      )}
                      {client.has_stock_shortage && (
                        <span title="Tem artigos sem stock reservado suficiente" data-testid={`encomenda-client-shortage-${idx}`}>
                          <Warning size={16} weight="fill" className="text-red-500 flex-shrink-0" />
                        </span>
                      )}
                      {client.has_cnt_pickup && (
                        <span title="Tem material para levantar no cliente (Reclamação)" data-testid={`encomenda-client-cnt-${idx}`}>
                          <Truck size={16} weight="fill" className="text-blue-600 flex-shrink-0" />
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-stone-400 mt-0.5">
                      Código {client.client_code_raw} · {client.item_count} artigo{client.item_count !== 1 ? 's' : ''}
                      {!isMatched && <span className="text-amber-600"> · sem ficha associada</span>}
                    </p>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <div className="text-right">
                      <p className="text-sm font-semibold text-[#009392]">{formatNum(client.total_m2)} m²</p>
                      <p className="text-xs text-stone-400 flex items-center gap-1 justify-end"><Scales size={12} />{formatNum(client.total_peso, 0)} kg</p>
                    </div>
                    {isOpen ? <CaretUp size={18} className="text-stone-400" /> : <CaretDown size={18} className="text-stone-400" />}
                  </div>
                </button>

                {isOpen && (
                  <div className="border-t border-stone-100">
                    <EncomendaOrdersTable items={client.items} idx={idx} />
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Modal de filtro global: mostra exatamente que clientes e que materiais estão em
          cada situação (Zombie/Atraso/Falta de Stock/Recolha), aberto ao clicar num ícone
          da legenda acima. */}
      <Dialog open={!!filterModal} onOpenChange={(open) => !open && setFilterModal(null)}>
        <DialogContent className="max-w-4xl" data-testid="encomendas-filter-modal">
          {filterModal && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {(() => { const Icon = FILTER_META[filterModal].icon; return <Icon size={20} weight="fill" className={FILTER_META[filterModal].color} />; })()}
                  {FILTER_META[filterModal].label}
                </DialogTitle>
                <DialogDescription>
                  {filteredItems.length} artigo{filteredItems.length !== 1 ? 's' : ''} de {new Set(filteredItems.map(i => i.clientName)).size} cliente{new Set(filteredItems.map(i => i.clientName)).size !== 1 ? 's' : ''}
                </DialogDescription>
              </DialogHeader>
              <div className="max-h-[60vh] overflow-y-auto -mx-1 px-1">
                {filteredItems.length === 0 ? (
                  <p className="text-sm text-stone-400 text-center py-8">Nenhum artigo nesta situação neste momento.</p>
                ) : (
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-white">
                      <tr className="text-stone-500 border-b border-stone-200">
                        <th className="text-left px-2 py-2 font-semibold">Cliente</th>
                        <th className="text-left px-2 py-2 font-semibold">Artigo</th>
                        <th className="text-left px-2 py-2 font-semibold">Descrição</th>
                        <th className="text-left px-2 py-2 font-semibold">Nº Encomenda</th>
                        <th className="text-right px-2 py-2 font-semibold">Dias</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredItems.map((item) => (
                        <tr key={item.rowKey} className="border-b border-stone-50" data-testid={`encomendas-filter-row-${item.rowKey}`}>
                          <td className="px-2 py-2 font-medium text-stone-800 whitespace-nowrap">
                            {item.clientId ? (
                              <button
                                type="button"
                                onClick={() => navigate(`/clients/${item.clientId}`)}
                                className="text-[#009392] hover:underline underline-offset-2 text-left"
                                data-testid={`encomendas-filter-client-link-${item.rowKey}`}
                              >
                                {item.clientName}
                              </button>
                            ) : (
                              item.clientName
                            )}
                          </td>
                          <td className="px-2 py-2 font-mono text-stone-700 whitespace-nowrap">{item.artigo}</td>
                          <td className="px-2 py-2 text-stone-600">{item.descricao}</td>
                          <td className="px-2 py-2 text-stone-500 whitespace-nowrap">{item.order_number || '—'}{item.order_ref ? ` (${item.order_ref})` : ''}</td>
                          <td className="px-2 py-2 text-right font-semibold text-stone-700 whitespace-nowrap">{item.days_since_order != null ? `${item.days_since_order}d` : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
